/**
 * <s-swatch-row> — variant image tiles (blocks/variant-picker.liquid).
 * - On phones the row is a single horizontal swipe line; after each product-form partial swap it scrolls
 *   the selected tile into view without moving the page.
 * - Phones pre-select the first available style like desktop (merchant 2026-11-03). The no-selection path below
 *   (html[data-variant-unchosen], prompt) stays as a safety net but no longer starts unchosen.
 *   While unchosen, Add to cart (incl. the sticky bar, which submits the same form) and the express
 *   payment buttons are held back: the page scrolls to the tiles and shows [data-select-prompt].
 */
const isPhone = () => window.matchMedia('(max-width: 47.999rem)').matches;
// Phones pre-select the first available style like desktop (merchant 2026-11-03), so material and price show at once.
let chosen = true;
let globalsBound = false;

function promptToChoose() {
  const prompt = document.querySelector('[data-product-form] [data-select-prompt]');
  if (prompt) prompt.hidden = false;
  const target = prompt ?? document.querySelector('[data-product-form] s-variant-picker');
  target?.scrollIntoView({
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    block: 'center',
  });
}

function bindGlobals() {
  if (globalsBound) return;
  globalsBound = true;
  document.addEventListener(
    'submit',
    (event) => {
      if (chosen || !isPhone() || !(event.target instanceof Element) || !event.target.closest('[data-product-form]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      promptToChoose();
    },
    true,
  );
  document.addEventListener(
    'click',
    (event) => {
      if (chosen || !isPhone() || !(event.target instanceof Element) || !event.target.closest('[data-payment-wrap]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      promptToChoose();
    },
    true,
  );
}

// The phone style sheet (s-variant-sheet.js) switches variants directly, without tapping a tile; it announces
// the choice here so Add to cart, the sticky bar label and express checkout are released (fix 2026-11-03).
document.addEventListener('fz:variant-chosen', () => {
  chosen = true;
  document.documentElement.removeAttribute('data-variant-unchosen');
  const prompt = document.querySelector('[data-product-form] [data-select-prompt]');
  if (prompt) prompt.hidden = true;
});

class SSwatchRow extends HTMLElement {
  #main = false;

  connectedCallback() {
    // Only the main product page column takes part in "no style pre-selected" (not quick-add dialogs).
    this.#main = !!this.closest('[data-quick-add-source]') && !this.closest('dialog');
    if (this.#main) {
      bindGlobals();
      document.documentElement.toggleAttribute('data-variant-unchosen', !chosen);
      this.addEventListener('click', this.#onClick);
    }
    if (this.#main && !chosen) return;
    requestAnimationFrame(() => {
      if (this.scrollWidth <= this.clientWidth) return;
      const checked = this.querySelector('input:checked');
      const tile = checked?.closest('label') ?? checked?.nextElementSibling;
      if (!(tile instanceof HTMLElement)) return;
      const left = tile.offsetLeft - (this.clientWidth - tile.offsetWidth) / 2;
      this.scrollTo({ left: Math.max(0, left), behavior: 'instant' });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    // A label tap also fires a synthetic click on its input; only the real tap counts.
    if (event.target instanceof HTMLInputElement) return;
    const label = event.target instanceof Element ? event.target.closest('label') : null;
    const input = label?.querySelector('input');
    if (!input || input.disabled) return;
    // Any tap keeps a style chosen (phones pre-select the first available style).
    chosen = true;
    document.documentElement.toggleAttribute('data-variant-unchosen', !chosen);
    const prompt = document.querySelector('[data-product-form] [data-select-prompt]');
    if (prompt && chosen) prompt.hidden = true;
  };
}

if (!customElements.get('s-swatch-row')) customElements.define('s-swatch-row', SSwatchRow);
